"use client";

import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { cva } from "../../lib/cva";
import type React from "react";
import { cn } from "../../lib/utils";
import { CHECKBOX_FILL_CLASS, CheckboxCheckGlyph } from "./checkbox-face";

/**
 * The box's footprint. Exported because a LIST that draws checkboxes has rows
 * that do not — a create row, a clear row — and they must still reserve the
 * column, or their glyph lands off the rail every other row shares. A literal
 * copy of the size at that call site drifts the moment this one changes.
 */
export const CHECKBOX_SIZE_CLASS = "size-4.5 sm:size-4";

// Unchecked is always `--background` in both modes — the box reads as a hole
// cut into the surface hosting it, never a filled well. Its resting hairline
// sits halfway between `--input` and `--border-strong`: louder than a text
// input at rest, while hover keeps a rung left to step up to. Radio draws the
// same rung — the two controls must not drift apart.
export const checkboxVariants = cva(
  CHECKBOX_SIZE_CLASS +
    " relative inline-flex shrink-0 items-center justify-center rounded-[.25rem] border border-[color-mix(in_oklab,var(--input),var(--border-strong)_50%)] bg-background not-dark:bg-clip-padding shadow-xs/5 outline-none ring-ring transition-[box-shadow,border-color] not-data-disabled:not-data-checked:not-aria-invalid:not-focus-visible:hover:border-border-strong before:pointer-events-none before:absolute before:inset-0 before:rounded-[3px] not-data-disabled:not-data-checked:not-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background aria-invalid:border-destructive/36 focus-visible:aria-invalid:border-destructive/64 focus-visible:aria-invalid:ring-destructive/48 data-disabled:cursor-not-allowed data-disabled:opacity-64 dark:focus-visible:aria-invalid:ring-destructive/24 dark:not-data-disabled:not-data-checked:not-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)] [[data-disabled],[data-checked],[aria-invalid]]:shadow-none",
);

export type CheckboxProps = CheckboxPrimitive.Root.Props & {
  /**
   * Render the box as a pure indicator, for a row that owns its own press.
   *
   * It keeps every resting, checked and invalid style and drops everything
   * else: no pointer events, no tab stop, no hidden form input, and no place
   * in the accessibility tree — the face is a `<span>` wearing the same data
   * attributes the real box publishes, so one recipe styles both. A second
   * control announcing the selection its row already announces is noise, one
   * nobody can reach — the row swallows the pointer — is a promise the
   * component cannot keep, and a `role="checkbox"` inside a row that is
   * itself an option or a menu item is ARIA nesting no screen reader can
   * resolve. The row carries the state; this draws it.
   */
  nonInteractive?: boolean;
};

/** The tick a checked box shows, in both the interactive and the drawn face. */
function CheckboxGlyph({
  indeterminate,
}: {
  indeterminate: boolean;
}): React.ReactElement {
  if (!indeterminate) return <CheckboxCheckGlyph />;
  return (
    <svg
      aria-hidden="true"
      className="size-3.5 sm:size-3"
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5.252 12h13.496" />
    </svg>
  );
}

export function Checkbox({
  className,
  nonInteractive = false,
  ...props
}: CheckboxProps): React.ReactElement {
  if (nonInteractive) {
    // Every prop that only a real Base UI root can honour is dropped here:
    // there is no hidden input to name, no press to report, and no form to
    // submit to. What is left is span markup the caller may still want (an
    // id, a style, a data attribute).
    const {
      checked,
      defaultChecked,
      indeterminate,
      disabled,
      onCheckedChange,
      name,
      form,
      readOnly,
      required,
      inputRef,
      parent,
      uncheckedValue,
      value,
      nativeButton,
      render,
      ...rest
    } = props;
    const isChecked = checked ?? defaultChecked ?? false;
    const state = indeterminate
      ? { "data-indeterminate": "" }
      : isChecked
        ? { "data-checked": "" }
        : { "data-unchecked": "" };
    return (
      <span
        aria-hidden
        className={cn(checkboxVariants(), "pointer-events-none", className)}
        data-slot="checkbox"
        {...(disabled ? { "data-disabled": "" } : {})}
        {...state}
        {...(rest as React.ComponentProps<"span">)}
      >
        {isChecked || indeterminate ? (
          <span
            className={cn(
              CHECKBOX_FILL_CLASS,
              indeterminate ? "text-foreground" : "bg-primary",
            )}
            data-slot="checkbox-indicator"
            {...state}
          >
            <CheckboxGlyph indeterminate={indeterminate === true} />
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <CheckboxPrimitive.Root
      className={cn(checkboxVariants(), className)}
      data-slot="checkbox"
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn(
          CHECKBOX_FILL_CLASS,
          "data-unchecked:hidden data-checked:bg-primary data-indeterminate:text-foreground",
        )}
        data-slot="checkbox-indicator"
        render={(
          props: React.ComponentProps<"span">,
          state: CheckboxPrimitive.Indicator.State,
        ) => (
          <span {...props}>
            <CheckboxGlyph indeterminate={state.indeterminate} />
          </span>
        )}
      />
    </CheckboxPrimitive.Root>
  );
}

export {
  CheckboxPrimitive,
};
